import type { CSSProperties, ReactNode } from 'react';

interface HudChipProps {
  children: ReactNode;
  /** Visual active state — drives both `data-active` (CSS hook) and `aria-pressed`. */
  active?: boolean;
  /** Optional team-coloured variant. 'away' uses the amber accent. */
  team?: 'home' | 'away';
  /** Accessible label override for buttons whose visible text is a glyph. */
  ariaLabel?: string;
  /** Tooltip text (browser `title`). */
  title?: string;
  /** Optional CSS class to layer on the standard thud__chip. */
  className?: string;
  /** Optional accent colour for the active state. When set, overrides
   *  the standard green-bright treatment via inline custom property
   *  `--chip-active-color` so the CSS can pick it up. Used by surfaces
   *  like the drill rail where each role has its own brand colour. */
  activeColor?: string;
  /** Forwarded to the underlying button for Playwright / RTL tests. */
  testId?: string;
  /** When true, the button is disabled (rejects clicks). */
  disabled?: boolean;
  onClick: () => void;
  type?: 'button' | 'submit';
}

/**
 * Single HUD toggle / action button. Two-axis state: `active` flips
 * the colour treatment (green for home, amber for away), and the
 * underlying `aria-pressed` is wired from the same prop so assistive
 * tech reads the state without an extra hand-rolled aria attribute on
 * every call site.
 *
 * Keeps the Phase E a11y wins (`aria-pressed`, `:focus-visible`) from
 * the original `.t2d-hud__chip` / `.t3d-hud__chip` markup — those
 * classes were 1:1 duplicates; this is the single source.
 */
export function HudChip({
  children,
  active,
  team,
  ariaLabel,
  title,
  className,
  activeColor,
  testId,
  disabled,
  onClick,
  type = 'button',
}: HudChipProps) {
  // When activeColor is set + chip is active, expose the colour as a CSS
  // custom property the stylesheet picks up. Falls through cleanly when
  // not active or not provided.
  const style = activeColor
    ? ({ ['--chip-active-color' as string]: activeColor } as CSSProperties)
    : undefined;
  return (
    <button
      type={type}
      className={className ? `thud__chip ${className}` : 'thud__chip'}
      data-active={active || undefined}
      data-team={team === 'away' ? 'away' : undefined}
      data-has-color={activeColor && active ? '1' : undefined}
      data-testid={testId}
      aria-pressed={active ?? false}
      aria-label={ariaLabel}
      title={title}
      disabled={disabled}
      onClick={onClick}
      style={style}
    >
      {children}
    </button>
  );
}
